import {
  InventoryAdjustmentReason,
  InventoryAdjustmentStatus,
  InventoryChargeToKind,
  InventoryCountCadence,
  InventoryCountScheduleState,
  InventoryCustodyStatus,
  InventoryItemNature,
  InventoryMovementClass,
  InventoryMovementKind,
  InventoryReceiptQcResult,
  InventoryReservationSource,
  InventoryReservationStatus,
  InventoryStockTakeApprovalRole,
  InventoryStockTakeStatus,
  InventoryTransferStatus,
  InventoryVendorReturnReason,
  InventoryVendorReturnStatus,
} from 'types/inventoryEnums.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'

export const ITEM_NATURE_TO_UI: { [k: number]: 'CONSUMABLE' | 'RETURNABLE' } = {
  [InventoryItemNature.Consumable]: 'CONSUMABLE',
  [InventoryItemNature.Returnable]: 'RETURNABLE',
}

export const MOVEMENT_KIND_LABEL: { [k: number]: string } = {
  [InventoryMovementKind.Receipt]: 'Receipt',
  [InventoryMovementKind.Issue]: 'Issue',
  [InventoryMovementKind.Assign]: 'Assign',
  [InventoryMovementKind.Return]: 'Return',
  [InventoryMovementKind.TransferOut]: 'Transfer out',
  [InventoryMovementKind.TransferIn]: 'Transfer in',
  [InventoryMovementKind.Adjustment]: 'Adjustment',
  [InventoryMovementKind.CountCorrection]: 'Count correction',
  [InventoryMovementKind.VendorReturn]: 'Vendor return',
}

export const RESERVATION_STATUS_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryReservationStatus.Active]: { label: 'Active', tone: 'accent' },
  [InventoryReservationStatus.Consumed]: { label: 'Consumed', tone: 'success' },
  [InventoryReservationStatus.Released]: { label: 'Released', tone: 'neutral' },
}

export const RESERVATION_SOURCE_LABEL: { [k: number]: string } = {
  [InventoryReservationSource.Request]: 'Request',
  [InventoryReservationSource.WorkOrder]: 'Work order',
  [InventoryReservationSource.Manual]: 'Manual',
}

export const ADJUSTMENT_REASON_LABEL: { [k: number]: string } = {
  [InventoryAdjustmentReason.Damage]: 'Damage',
  [InventoryAdjustmentReason.Theft]: 'Theft',
  [InventoryAdjustmentReason.CountVariance]: 'Count variance',
  [InventoryAdjustmentReason.Waste]: 'Waste',
  [InventoryAdjustmentReason.Expiry]: 'Expiry',
}

export const ADJUSTMENT_STATUS_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryAdjustmentStatus.Open]: { label: 'Open', tone: 'warning' },
  [InventoryAdjustmentStatus.Posted]: { label: 'Posted', tone: 'success' },
}

export const STOCK_TAKE_STATUS_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryStockTakeStatus.Scheduled]: { label: 'Scheduled', tone: 'info' },
  [InventoryStockTakeStatus.InProgress]: { label: 'In progress', tone: 'accent' },
  [InventoryStockTakeStatus.AwaitingApproval]: { label: 'Awaiting approval', tone: 'warning' },
  [InventoryStockTakeStatus.Completed]: { label: 'Completed', tone: 'success' },
}

export const TRANSFER_STATUS_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryTransferStatus.Draft]: { label: 'Draft', tone: 'neutral' },
  [InventoryTransferStatus.Dispatched]: { label: 'Dispatched', tone: 'accent' },
  [InventoryTransferStatus.InTransit]: { label: 'In transit', tone: 'warning' },
  [InventoryTransferStatus.Received]: { label: 'Received', tone: 'success' },
}

export const CUSTODY_STATUS_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryCustodyStatus.Ok]: { label: 'Ok', tone: 'success' },
  [InventoryCustodyStatus.Overdue]: { label: 'Overdue', tone: 'danger' },
  [InventoryCustodyStatus.Returned]: { label: 'Returned', tone: 'neutral' },
}

export const CUSTODY_UI_STATUS_META: {
  [k: string]: { tone: IStatusPill['status']; dot: boolean }
} = {
  OK: { tone: 'success', dot: false },
  OVERDUE: { tone: 'danger', dot: true },
  RETURNED: { tone: 'success', dot: false },
}

export const COUNT_CADENCE_LABEL: { [k: number]: string } = {
  [InventoryCountCadence.Weekly]: 'Weekly',
  [InventoryCountCadence.Monthly]: 'Monthly',
  [InventoryCountCadence.Quarterly]: 'Quarterly',
}

export const COUNT_SCHEDULE_STATE_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryCountScheduleState.Ok]: { label: 'Ok', tone: 'success' },
  [InventoryCountScheduleState.Counting]: { label: 'Counting', tone: 'accent' },
  [InventoryCountScheduleState.Overdue]: { label: 'Overdue', tone: 'danger' },
}

export const VENDOR_RETURN_STATUS_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryVendorReturnStatus.Draft]: { label: 'Draft', tone: 'neutral' },
  [InventoryVendorReturnStatus.Sent]: { label: 'Sent', tone: 'accent' },
  [InventoryVendorReturnStatus.Credited]: { label: 'Credited', tone: 'success' },
  [InventoryVendorReturnStatus.Replaced]: { label: 'Replaced', tone: 'success' },
}

export const VENDOR_RETURN_REASON_LABEL: { [k: number]: string } = {
  [InventoryVendorReturnReason.QcReject]: 'QC reject',
  [InventoryVendorReturnReason.ShortShip]: 'Short ship',
  [InventoryVendorReturnReason.Warranty]: 'Warranty',
}

export const RECEIPT_QC_RESULT_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryReceiptQcResult.Pass]: { label: 'Pass', tone: 'success' },
  [InventoryReceiptQcResult.Rejected]: { label: 'Rejected', tone: 'danger' },
  [InventoryReceiptQcResult.Pending]: { label: 'Pending', tone: 'warning' },
}

export const CHARGE_TO_KIND_LABEL: { [k: number]: string } = {
  [InventoryChargeToKind.CostCenter]: 'Cost center',
  [InventoryChargeToKind.Machine]: 'Machine',
  [InventoryChargeToKind.WorkOrder]: 'Work order',
}

export const MOVEMENT_CLASS_META: {
  [k: number]: { label: string; tone: IStatusPill['status'] }
} = {
  [InventoryMovementClass.Active]: { label: 'Active', tone: 'success' },
  [InventoryMovementClass.Slow]: { label: 'Slow', tone: 'warning' },
  [InventoryMovementClass.Obsolete]: { label: 'Obsolete', tone: 'danger' },
}

export const STOCK_TAKE_APPROVAL_ROLE_LABEL: { [k: number]: string } = {
  [InventoryStockTakeApprovalRole.InventoryManager]: 'Inventory manager',
  [InventoryStockTakeApprovalRole.Supervisor]: 'Supervisor',
}

export const COUNTER_KIND_META: {
  [k: string]: { label: string; tone: IStatusPill['status'] }
} = {
  ISSUED: { label: 'Issued', tone: 'accent' },
  ASSIGNED: { label: 'Assigned', tone: 'info' },
  RETURNED: { label: 'Returned', tone: 'success' },
}

export const COUNTER_NATURE_META: {
  [k: string]: { label: string; tone: IStatusPill['status'] }
} = {
  CONSUMABLE: { label: 'Consumable', tone: 'neutral' },
  RETURNABLE: { label: 'Returnable', tone: 'accent' },
}
